import { useState } from "react";
import { Table, Tag, Button, Modal, Form, Select, InputNumber, DatePicker, Input, message } from "antd";
import { ShieldCheck, Plus, Zap } from "lucide-react";
import type { ColumnType } from "antd/es/table";
import dayjs from "dayjs";
import ButtonWrapper from "../../components/reuseComp/ButtonWrapper";
import { useNMWList, useNMWRunCheck, useNMWBulkCheck } from "../../../hooks/api/payslips/hooks";
import { useFieldWorkerList } from "../../../hooks/api/field-workers/hooks";

const formatCurrency = (v: any) =>
  v != null ? `£${Number(v).toLocaleString("en-GB", { minimumFractionDigits: 2 })}` : "—";

const formatDate = (v: any) => (v ? dayjs(v).format("DD MMM YYYY") : "—");

const complianceColor = (s: string) => {
  if (!s) return "default";
  const sl = s.toLowerCase();
  if (sl === "compliant" || sl === "pass" || sl === "passed") return "green";
  if (sl === "warning" || sl === "review") return "orange";
  if (sl === "non-compliant" || sl === "fail" || sl === "failed") return "red";
  return "default";
};

function RunCheckModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [form] = Form.useForm();
  const [msgApi, ctx] = message.useMessage();
  const { data: workerRes } = useFieldWorkerList({ page: 1, limit: 100 });
  const workers = (workerRes?.data as any[]) ?? [];

  const runCheck = useNMWRunCheck({
    onSuccess: (res) => {
      msgApi.success(res?.message || "Check complete.");
      form.resetFields();
      onClose();
    },
    onError: (e) => msgApi.error(e?.response?.data?.message || "Failed"),
  });

  return (
    <Modal
      open={open}
      onCancel={() => { onClose(); form.resetFields(); }}
      title="Run NMW Check"
      width={480}
      footer={[
        <Button key="cancel" onClick={() => { onClose(); form.resetFields(); }}>Cancel</Button>,
        <Button key="save" type="primary" loading={runCheck.isPending} onClick={() => form.submit()}>Run Check</Button>,
      ]}
    >
      {ctx}
      <Form
        form={form}
        layout="vertical"
        size="middle"
        className="pt-2"
        onFinish={(vals) => {
          runCheck.mutate({
            user_id: vals.user_id,
            effective_rate: vals.effective_rate,
            check_date: vals.check_date ? vals.check_date.format("YYYY-MM-DD") : undefined,
            age_bracket: vals.age_bracket || undefined,
            notes: vals.notes || undefined,
          });
        }}
      >
        <Form.Item name="user_id" label="Worker" rules={[{ required: true, message: "Select a worker" }]}>
          <Select
            showSearch
            placeholder="Select worker…"
            filterOption={(v, o) => (o?.label as string)?.toLowerCase().includes(v.toLowerCase())}
            options={workers.map((w: any) => ({
              value: w.id,
              label: [w.first_name, w.last_name].filter(Boolean).join(" ") || w.email,
            }))}
          />
        </Form.Item>
        <Form.Item
          name="effective_rate"
          label="Effective Pay Rate (£/hr)"
          rules={[{ required: true, message: "Pay rate is required" }]}
        >
          <InputNumber className="w-full" min={0} step={0.01} prefix="£" />
        </Form.Item>
        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item name="check_date" label="Check Date">
            <DatePicker className="w-full" placeholder="Today" />
          </Form.Item>
          <Form.Item name="age_bracket" label="Age Bracket (optional)">
            <Input placeholder="e.g. 21+ / auto-detect" />
          </Form.Item>
        </div>
        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function BulkCheckModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [form] = Form.useForm();
  const [msgApi, ctx] = message.useMessage();

  const bulkCheck = useNMWBulkCheck({
    onSuccess: (res) => {
      msgApi.success(res?.message || "Bulk check complete.");
      form.resetFields();
      onClose();
    },
    onError: (e) => msgApi.error(e?.response?.data?.message || "Failed"),
  });

  return (
    <Modal
      open={open}
      onCancel={() => { onClose(); form.resetFields(); }}
      title="Bulk NMW Check — All Active Field Workers"
      width={440}
      footer={[
        <Button key="cancel" onClick={() => { onClose(); form.resetFields(); }}>Cancel</Button>,
        <Button key="save" type="primary" loading={bulkCheck.isPending} onClick={() => form.submit()}>Run Bulk Check</Button>,
      ]}
    >
      {ctx}
      <p className="text-slate-500 text-sm mb-4">
        Runs an NMW compliance check for every active field worker against their on-file hourly rate.
      </p>
      <Form
        form={form}
        layout="vertical"
        size="middle"
        onFinish={(vals) => {
          bulkCheck.mutate({
            check_date: vals.check_date ? vals.check_date.format("YYYY-MM-DD") : undefined,
            age_bracket: vals.age_bracket || undefined,
          });
        }}
      >
        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item name="check_date" label="Check Date">
            <DatePicker className="w-full" placeholder="Today" />
          </Form.Item>
          <Form.Item name="age_bracket" label="Age Bracket (optional)">
            <Input placeholder="e.g. 21+ / auto-detect" />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
}

const NMWPage = () => {
  const [page, setPage] = useState(1);
  const [runCheckOpen, setRunCheckOpen] = useState(false);
  const [bulkCheckOpen, setBulkCheckOpen] = useState(false);
  const limit = 20;

  const { data: res, isLoading } = useNMWList({ page, limit });
  const records: any[] = (res?.data as any) ?? [];
  const total = Number(res?.extra?.total ?? 0);

  const columns: ColumnType<any>[] = [
    {
      title: "Worker",
      key: "worker",
      render: (_: any, r: any) =>
        r.worker_name || r.worker || r.name || r.employee_name || "—",
      width: 180,
    },
    {
      title: "Pay Rate (£/hr)",
      key: "pay_rate",
      render: (_: any, r: any) =>
        r.effective_rate != null ? `£${Number(r.effective_rate).toFixed(2)}/hr` : "—",
      width: 130,
    },
    {
      title: "NMW Rate (£/hr)",
      key: "nmw_rate",
      render: (_: any, r: any) =>
        r.nmw_rate != null ? `£${Number(r.nmw_rate).toFixed(2)}/hr` : "—",
      width: 130,
    },
    {
      title: "Shortfall",
      key: "shortfall",
      render: (_: any, r: any) =>
        r.shortfall != null && Number(r.shortfall) > 0 ? (
          <span className="text-red-600 font-medium">{formatCurrency(r.shortfall)}/hr</span>
        ) : (
          "—"
        ),
      width: 120,
    },
    {
      title: "Compliance",
      key: "compliance",
      render: (_: any, r: any) => {
        // is_compliant comes back as a MySQL tinyint(1) — 1/0 (sometimes true/false) — never
        // as a string like "compliant"/"non-compliant", so the field must be read as a boolean.
        if (r.is_compliant === undefined || r.is_compliant === null) return "—";
        const compliant = r.is_compliant === 1 || r.is_compliant === true;
        return (
          <Tag color={complianceColor(compliant ? "compliant" : "non-compliant")}>
            {compliant ? "COMPLIANT" : "NON-COMPLIANT"}
          </Tag>
        );
      },
      width: 130,
    },
    {
      title: "Checked",
      key: "checked",
      render: (_: any, r: any) => formatDate(r.check_date ?? r.created_at),
      width: 120,
    },
    {
      title: "Notes",
      key: "notes",
      render: (_: any, r: any) => r.notes || "—",
      width: 160,
    },
  ];

  return (
    <div className="p-6 min-h-screen">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck size={20} className="text-slate-500" />
            <h1 className="text-xl font-semibold text-slate-800">NMW Compliance</h1>
          </div>
          <p className="text-slate-500 text-sm mt-0.5">
            National Minimum Wage compliance records
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button icon={<Zap size={14} />} onClick={() => setBulkCheckOpen(true)}>
            Bulk Check
          </Button>
          <ButtonWrapper type="primary" icon={<Plus size={15} />} onClick={() => setRunCheckOpen(true)} className="flex items-center gap-2">
            Run Check
          </ButtonWrapper>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <Table
          dataSource={records}
          columns={columns}
          rowKey={(r) => r.id ?? r._id ?? Math.random()}
          loading={isLoading}
          locale={{
            emptyText: (
              <div className="py-12 text-center text-slate-400">
                <ShieldCheck size={40} className="mx-auto mb-3 opacity-30" />
                <p className="font-medium">No NMW records found</p>
                <p className="text-sm mt-1">
                  NMW compliance data will appear here once records are generated.
                </p>
              </div>
            ),
          }}
          pagination={{
            current: page,
            pageSize: limit,
            total,
            showTotal: (t) => `${t} records`,
            showSizeChanger: false,
            onChange: (p) => setPage(p),
          }}
          scroll={{ x: 1000 }}
          size="small"
        />
      </div>

      <RunCheckModal open={runCheckOpen} onClose={() => setRunCheckOpen(false)} />
      <BulkCheckModal open={bulkCheckOpen} onClose={() => setBulkCheckOpen(false)} />
    </div>
  );
};

export default NMWPage;
